/**
 * G3/G4 — Browse Home Obchody results from public customer-sales-points.
 * Empty-q: paginated pages. With-q: parent supplies drain+client-filtered rows.
 */
import { useTranslation } from 'react-i18next';
import {
  toSameOriginCatalogImageUrl,
  normalizeLogoChipBackgroundSettings,
  normalizeLogoChipRimSettings,
  resolveLocalizedName,
} from 'pi-kiosk-shared';
import { useTheme } from '../../app/ThemeProvider.js';
import { Button } from '../../shared/ui/Button.js';
import { Card } from '../../shared/ui/Card.js';
import {
  DirectoryResults,
  DirectoryResultsSkeleton,
} from '../../shared/ui/DirectoryResults.js';
import {
  DIRECTORY_EMPTY_PANEL_MAX_CLASS,
  DIRECTORY_NEARBY_INSET_ROW_CLASS,
} from '../../shared/ui/directoryPickerLayout.js';
import { useStoreDirectoryThumb } from '../../shared/ui/useDirectoryEntityThumb.js';
import {
  directoryThumbFetchPriority,
  directoryThumbLoading,
} from '../../shared/ui/directoryThumbLoading.js';
import { ShopDirectoryRowView } from '../shops/ShopDirectoryRowView.js';
import { resolveSalesPointDescription } from '../shops/resolveSalesPointDescription.js';
import { resolveDirectoryShopOpenAction } from '../shops/resolveDirectoryShopOpenAction.js';
import { resolveShopLocationLabelWithT } from '../shops/resolveShopLocationLabel.js';
import type { PublicCustomerSalesPointDTO } from '../shops/publicSalesPointsApi.js';

export type BrowseShopDirectoryResultsProps = {
  readonly items: readonly PublicCustomerSalesPointDTO[];
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly onRetry: () => void;
  readonly hasNextPage?: boolean;
  readonly isFetchingNextPage?: boolean;
  readonly onLoadMore?: () => void;
  /** When false, omit Obchody (N) H2 (Vše section supplies its own header). */
  readonly showHeading?: boolean;
  readonly testId?: string;
};

export function BrowseShopDirectoryResults(
  props: BrowseShopDirectoryResultsProps,
): JSX.Element {
  const {
    items,
    isPending,
    isError,
    onRetry,
    hasNextPage = false,
    isFetchingNextPage = false,
    onLoadMore,
    showHeading = true,
    testId = 'customer-browse-shop-directory',
  } = props;
  const { t, i18n } = useTranslation('customer');

  if (isPending) {
    return (
      <div data-testid={testId} data-state="loading">
        <DirectoryResultsSkeleton
          density="list"
          listLayout="inset"
          size="md"
          rowDensity="compact"
          count={4}
          testId={`${testId}-skeleton`}
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div data-testid={testId} data-state="error">
        <Card padded className={DIRECTORY_EMPTY_PANEL_MAX_CLASS}>
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {t('browse.discovery.shopDirectoryError')}
          </p>
          <Button
            type="button"
            intent="secondary"
            block
            className="mt-4"
            data-testid={`${testId}-retry`}
            onClick={onRetry}
          >
            {t('shared.retry')}
          </Button>
        </Card>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div data-testid={testId} data-state="empty">
        <Card padded className={DIRECTORY_EMPTY_PANEL_MAX_CLASS}>
          <p
            className="text-sm text-[var(--color-on-surface-muted)]"
            data-testid={`${testId}-empty-copy`}
          >
            {t('browse.discovery.shopDirectoryEmpty')}
          </p>
        </Card>
      </div>
    );
  }

  const shopCount = items.length;

  return (
    <div
      className="space-y-3"
      data-testid={testId}
      data-state="ready"
      data-shop-count={String(shopCount)}
    >
      {showHeading ? (
        <h2
          id={`${testId}-heading`}
          className="text-base font-semibold text-[var(--color-on-surface)]"
          data-testid={`${testId}-heading`}
        >
          {t('browse.discovery.shopDirectoryHeading', {
            count: shopCount,
          })}
        </h2>
      ) : null}
      <DirectoryResults
        density="list"
        listLayout="inset"
        testId={`${testId}-results`}
        aria-live="polite"
        aria-labelledby={showHeading ? `${testId}-heading` : undefined}
      >
        {items.map((item, index) => (
          <BrowseShopDirectoryRow
            key={`${item.tenantCode}-${item.salesPointId}`}
            item={item}
            index={index}
            language={i18n.language}
          />
        ))}
      </DirectoryResults>
      {hasNextPage && onLoadMore !== undefined ? (
        <div className="pt-2">
          <Button
            type="button"
            intent="secondary"
            size="md"
            block
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
            data-testid={`${testId}-load-more`}
          >
            {isFetchingNextPage
              ? t('shared.loading')
              : t('browse.discovery.loadMore')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function BrowseShopDirectoryRow({
  item,
  index,
  language,
}: {
  readonly item: PublicCustomerSalesPointDTO;
  readonly index: number;
  readonly language: string;
}): JSX.Element {
  const { t } = useTranslation('customer');
  const { effectiveTheme } = useTheme();
  const displayName = resolveLocalizedName(item.name, item.nameLocales, language);
  const locationLabel = resolveShopLocationLabelWithT(
    {
      customerPresence: item.customerPresence,
      location: item.location,
      locationMissingKey: 'retail.entry.shops.locationMissing',
    },
    t,
  );
  const displayDescription = resolveSalesPointDescription(
    item.description,
    item.descriptionLocales,
    language,
  );
  const imageUrl = toSameOriginCatalogImageUrl(item.imageUrl, {
    tenantCode: item.tenantCode,
  });
  const imageUrlDark = toSameOriginCatalogImageUrl(item.imageUrlDark, {
    tenantCode: item.tenantCode,
  });
  const tenantLogoUrl = toSameOriginCatalogImageUrl(item.tenantLogoUrl, {
    tenantCode: item.tenantCode,
  });
  const tenantLogoUrlDark = toSameOriginCatalogImageUrl(item.tenantLogoUrlDark, {
    tenantCode: item.tenantCode,
  });

  const { thumbProps } = useStoreDirectoryThumb({
    imageUrl,
    imageUrlDark,
    tenantLogoUrl,
    tenantLogoUrlDark,
    monogram: {
      id: item.salesPointId,
      displayName,
      code: item.code,
    },
    showTenantChip: item.showTenantLogoOnStorePhoto ?? true,
    // G2 / D23 — search Obchody compact identity (52–60 md), not picker lg.
    size: 'md',
    logoChipRim: normalizeLogoChipRimSettings(item),
    logoChipBackground: normalizeLogoChipBackgroundSettings(item),
    logoChipRimTheme: effectiveTheme,
    loading: directoryThumbLoading(index),
    fetchPriority: directoryThumbFetchPriority(index),
    testId: `customer-browse-shop-thumb-${item.salesPointId}`,
  });

  const rowPressHref =
    resolveDirectoryShopOpenAction({
      customerShopUrl: item.customerShopUrl,
      targetTenantCode: item.tenantCode,
    })?.url;

  return (
    <li
      className={DIRECTORY_NEARBY_INSET_ROW_CLASS}
      data-testid={`customer-browse-shop-item-${item.salesPointId}`}
    >
      <ShopDirectoryRowView
        title={displayName}
        tenantLabel={item.tenantName.length > 0 ? item.tenantName : null}
        location={locationLabel}
        description={displayDescription}
        thumb={thumbProps}
        rowPressHref={rowPressHref}
        rowPressTestId={`customer-browse-shop-open-${item.salesPointId}`}
        showChevron
        density="compact"
        testId={`customer-browse-shop-row-${item.salesPointId}`}
      />
    </li>
  );
}
